import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import {
  Sweetpass,
  useSweetpassCheckoutMachine,
  useSweetpassContent,
  type SweetpassContent,
} from '@order/features-loyalty';
import { getStripeKeyPrefix } from '@order/integrations-stripe';

import { NotFoundView } from '../../../../components';
import { SweetpassCheckoutReady } from '../SweetpassCheckoutReady';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckout = () => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const params = useLocalSearchParams<{ sweetpassId?: string }>();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const navigateToRewards = useCallback(() => {
    router.navigate('/rewards');
  }, [router]);

  // ─── State ─────────────────────────────────────────────────────────────────

  const state = useSweetpassCheckoutMachine({
    sweetpassId: params.sweetpassId || MOCK_SWEETPASS_ID,
    amount: SWEETPASS_AMOUNT,
    onPurchaseSucceeded: navigateToRewards,
  });

  const { isLoading, hasFailedToLoad, purchaseAttempt, stripeSession, setStripeFailedToLoad } = state; // prettier-ignore

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<SweetpassContent>({ contentType: 'sweetpass', include: 2 }); // prettier-ignore
  const content = useSweetpassContent(entry?.data?.fields);

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoading || entry.fetching) {
    return <Sweetpass.Checkout.LoadingPlaceholder />;
  }

  if (hasFailedToLoad) {
    return (
      <NotFoundView
        title={formatMessage(messages.failedToLoadTitle)}
        description={formatMessage(messages.failedToLoadDescription)}
      />
    );
  }

  return (
    <Sweetpass.Checkout.PaymentProvider
      amount={SWEETPASS_AMOUNT}
      sessionId={stripeSession}
      onFailedToLoadStripe={setStripeFailedToLoad}
    >
      <SweetpassCheckoutReady
        key={getStripeKeyPrefix(purchaseAttempt)}
        state={state}
        content={content}
      />
    </Sweetpass.Checkout.PaymentProvider>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────
// These will come from the available passes query once the backend is ready.

const MOCK_SWEETPASS_ID = 'MOCK_SWEETPASS_ID';
const SWEETPASS_AMOUNT = 1000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  failedToLoadTitle: {
    defaultMessage: 'Oops! Something went wrong!',
    description: 'Sweetpass Checkout | Failed To Load | Title',
  },
  failedToLoadDescription: {
    defaultMessage: 'Please reload the app or try again later.',
    description: 'Sweetpass Checkout | Failed To Load | Description',
  },
});
